import { ApiError, apiFetch } from "@pontimed/api-client";
import { loadStripe } from "@stripe/stripe-js";

/**
 * A subscription change billed at once (plan upgrade, more seats) whose
 * payment the bank wants the customer to confirm (3-D Secure): the API answers
 * 402 `payment_requires_action` with the PaymentIntent's `clientSecret` (and
 * the active Stripe mode's `publishableKey`) and holds the change on Stripe
 * until it is paid. Confirming it here applies the change on Stripe; the
 * caller then sends the SAME request again (sendAfterCardAction), which finds
 * Stripe already changed and records it without charging a second time.
 */
export function requiredCardAction(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { code?: string; clientSecret?: string } | null;
  return body?.code === "payment_requires_action" && body.clientSecret ? body.clientSecret : null;
}

/** The publishable key a 402 `payment_requires_action` carries, when it does. */
export function cardActionPublishableKey(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { publishableKey?: string | null } | null;
  return body?.publishableKey || null;
}

/** The customer did not complete the bank's confirmation — nothing was changed. */
export class CardActionDeclined extends Error {
  constructor() {
    super("The payment was not confirmed");
    this.name = "CardActionDeclined";
  }
}

/** Confirmed, and Stripe still had not applied the change after the retries. */
export class CardActionStillProcessing extends Error {
  constructor() {
    super("The payment is still being processed");
    this.name = "CardActionStillProcessing";
  }
}

/**
 * Run the bank's confirmation for `clientSecret`; true when it went through.
 * The key is the one the API answered with — the ACTIVE Stripe mode's (the
 * admin switches test / live in the DB) — and the build-time key only when an
 * older answer carries none.
 */
export async function confirmCardAction(
  clientSecret: string,
  publishableKey?: string | null,
): Promise<boolean> {
  const key = publishableKey || (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined);
  if (!key) return false;
  const stripe = await loadStripe(key);
  if (!stripe) return false;
  const { error } = await stripe.handleNextAction({ clientSecret });
  return !error;
}

const PROCESSING_RETRIES = 5;
const PROCESSING_DELAY_MS = 1500;

/** 409 `payment_processing`: Stripe still holds the change while the payment goes through. */
export function isPaymentProcessing(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  return (err.body as { code?: string } | null)?.code === "payment_processing";
}

/**
 * Send the change again after the customer confirmed the payment. Stripe may
 * take a moment to apply it; until then the API answers 409
 * `payment_processing` — retried briefly, then CardActionStillProcessing.
 */
export async function sendAfterCardAction<T>(send: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await send();
    } catch (err) {
      if (!isPaymentProcessing(err)) throw err;
      if (attempt >= PROCESSING_RETRIES) throw new CardActionStillProcessing();
      await new Promise((resolve) => setTimeout(resolve, PROCESSING_DELAY_MS));
    }
  }
}

/** What an earlier held change is, as the 409 `payment_processing` describes it. */
export interface PendingChange {
  clientSecret: string;
  publishableKey: string | null;
  change: {
    planCode: string | null;
    planName: unknown;
    quantity: number | null;
    interval: "monthly" | "annual" | null;
    amountCents: number | null;
    currency: string | null;
  } | null;
}

/** The customer's answer about an earlier held change (usePendingPaymentPrompt). */
export type PendingChoice = "pay" | "discard" | "cancel";

/** The customer closed the prompt about an earlier held change: nothing was sent. */
export class PendingChoiceCancelled extends Error {
  constructor() {
    super("The pending payment was left as it is");
    this.name = "PendingChoiceCancelled";
  }
}

/**
 * The earlier change a 409 `payment_processing` names, when Stripe still holds
 * it for a payment the bank wants confirmed (3-D Secure): its `clientSecret`,
 * the key to confirm it with and what it is. Null otherwise (the payment is
 * settling).
 */
export function pendingChangePayment(err: unknown): PendingChange | null {
  if (!isPaymentProcessing(err)) return null;
  const body = (err as ApiError).body as Partial<PendingChange> | null;
  return body?.clientSecret
    ? {
        clientSecret: body.clientSecret,
        publishableKey: body.publishableKey ?? null,
        change: body.change ?? null,
      }
    : null;
}

/**
 * What discarding a held change found: discarded; its payment went through
 * after all (Stripe applied the change, or the invoice was paid in between);
 * or nothing certain — the page is refreshed, never "nothing was changed".
 */
export type DiscardOutcome = "discarded" | "went_through" | "unknown";

/** Neither discarded nor known to be through: refresh to see what happened. */
export class CardActionOutcomeUnknown extends Error {
  constructor() {
    super("The outcome of the payment is not known");
    this.name = "CardActionOutcomeUnknown";
  }
}

/** The PaymentIntent id a client secret belongs to (`pi_…_secret_…`). */
function paymentIntentIdOf(clientSecret: string | undefined): string | undefined {
  const id = clientSecret?.split("_secret_")[0];
  return id?.startsWith("pi_") ? id : undefined;
}

/**
 * Discard the change Stripe holds for a payment the customer did not confirm:
 * its invoice is voided and the change dropped, so the next change is not
 * refused with `payment_processing` for up to a day. The held payment's
 * secret lets the server tell whether it went through after all.
 */
export async function discardPendingPayment(
  subscriptionId: string,
  run: <T>(fn: () => Promise<T>) => Promise<T> = (fn) => fn(),
  clientSecret?: string,
): Promise<DiscardOutcome> {
  try {
    const res = await run(() =>
      apiFetch.post<{ discarded?: boolean; applied?: boolean }>(
        "/api/account/billing/cancel-pending-payment",
        { subscriptionId, paymentIntentId: paymentIntentIdOf(clientSecret) },
      ),
    );
    if (res.applied) return "went_through";
    return res.discarded ? "discarded" : "unknown";
  } catch (err) {
    return isPaymentProcessing(err) ? "went_through" : "unknown";
  }
}

/** A discard's outcome as what the customer is told: through, or not known. */
function throwUnlessDiscarded(outcome: DiscardOutcome): void {
  if (outcome === "went_through") throw new CardActionStillProcessing();
  if (outcome === "unknown") throw new CardActionOutcomeUnknown();
}

/**
 * Send a change billed at once (plan, seats, interval), with every bank
 * confirmation it may meet:
 *   - an EARLIER change still held for its 3-D Secure (409
 *     `payment_processing` with its secret): the customer is shown what it is
 *     (`ask`) and pays it — that change applies — or discards it; then this
 *     one is sent. Closing the prompt: PendingChoiceCancelled, nothing sent.
 *     A discard that finds the earlier payment went through (or cannot tell)
 *     sends nothing on top: CardActionStillProcessing / CardActionOutcomeUnknown;
 *   - this change needs 3-D Secure: confirmed, then sent again
 *     (sendAfterCardAction). Not confirmed → discarded and CardActionDeclined —
 *     or CardActionStillProcessing when its payment went through after all,
 *     CardActionOutcomeUnknown when that is not known.
 */
export async function sendChangeWithCardAction<T>(
  send: () => Promise<T>,
  discard: (clientSecret: string) => Promise<DiscardOutcome>,
  ask: (pending: PendingChange) => Promise<PendingChoice>,
): Promise<T> {
  const ownAction = async (err: unknown): Promise<T> => {
    const clientSecret = requiredCardAction(err);
    if (!clientSecret) throw err;
    if (!(await confirmCardAction(clientSecret, cardActionPublishableKey(err)))) {
      throwUnlessDiscarded(await discard(clientSecret));
      throw new CardActionDeclined();
    }
    return sendAfterCardAction(send);
  };
  try {
    return await send();
  } catch (err) {
    const earlier = pendingChangePayment(err);
    if (!earlier) return ownAction(err);
    const choice = await ask(earlier);
    if (choice === "cancel") throw new PendingChoiceCancelled();
    if (
      choice === "discard" ||
      !(await confirmCardAction(earlier.clientSecret, earlier.publishableKey))
    ) {
      throwUnlessDiscarded(await discard(earlier.clientSecret));
    }
    try {
      return await sendAfterCardAction(send);
    } catch (again) {
      return ownAction(again);
    }
  }
}
